import type { Identifier } from "ra-core";
import { useGetMany } from "ra-core";
import { useMemo } from "react";

import { findDealLabel } from "../../deals/dealUtils";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Company, Contact, Deal } from "../../types";
import type { AttentionItem } from "./homeModel";
import { formatPersonName } from "../../misc/personName";

export interface AttentionLookup {
  contactName: (id?: Identifier | null) => string | null;
  companyName: (id?: Identifier | null) => string | null;
  dealName: (id?: Identifier | null) => string | null;
  stageLabel: (stage?: string) => string | null;
  taskTypeLabel: (type?: string) => string | null;
  /** Company of an item: its own, or its deal's. */
  itemCompany: (item: AttentionItem) => string | null;
}

const unique = (ids: (Identifier | null | undefined)[]) => [
  ...new Set(ids.filter((id): id is Identifier => id != null).map(String)),
];

/**
 * Names shown in the inbox rows: two getMany calls (contacts, companies) for
 * the visible items; deal names come from the already loaded deals list.
 */
export const useAttentionLookup = (
  items: readonly AttentionItem[],
  deals: readonly Pick<Deal, "id" | "name" | "company_id">[],
): AttentionLookup => {
  const { dealStages, taskTypes } = useConfigurationContext();
  const dealsById = useMemo(
    () => new Map(deals.map((deal) => [String(deal.id), deal])),
    [deals],
  );
  const contactIds = unique(items.map((item) => item.contactId));
  const companyIds = unique(
    items.flatMap((item) => [
      item.companyId,
      item.dealId != null
        ? dealsById.get(String(item.dealId))?.company_id
        : undefined,
    ]),
  );
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const { data: companies } = useGetMany<Company>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );

  return useMemo(() => {
    const byId = <T extends { id: Identifier }>(list?: T[]) =>
      new Map((list ?? []).map((record) => [String(record.id), record]));
    const contactMap = byId(contacts);
    const companyMap = byId(companies);
    const companyName = (id?: Identifier | null) =>
      id == null ? null : (companyMap.get(String(id))?.name ?? null);
    return {
      contactName: (id) => {
        const contact = id == null ? undefined : contactMap.get(String(id));
        return contact ? formatPersonName(contact) || null : null;
      },
      companyName,
      itemCompany: (item) =>
        companyName(
          item.companyId ??
            (item.dealId != null
              ? dealsById.get(String(item.dealId))?.company_id
              : undefined),
        ),
      dealName: (id) =>
        id == null ? null : (dealsById.get(String(id))?.name ?? null),
      stageLabel: (stage) =>
        stage ? findDealLabel(dealStages, stage) || null : null,
      taskTypeLabel: (type) =>
        !type || type === "none"
          ? null
          : (taskTypes.find((item) => item.value === type)?.label ?? null),
    };
  }, [contacts, companies, dealsById, dealStages, taskTypes]);
};
